/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import isFunction from 'lodash/isFunction';
import merge from 'lodash/merge';
import noop from 'lodash/noop';

import {mergeRefs} from '@edna/utils';

import {useMeasure} from '../utils';
import {FormField, Header, Label} from './primitives';
import * as S from './style';
import {
  TComponentProps,
  TComponentRef,
  TFieldHandlers,
  TNewProps,
  TProps,
  TSettings,
} from './types';

export type {TComponentRef};

const defaultSettings: TSettings = {
  noMargin: false,
  showHeader: true,
  showError: true,
  isLabelledBy: false,
};

type TFieldErrorProps = {
  className?: string;
  width?: string;
};

const FieldError: React.FC<TFieldErrorProps> = ({children, width = '100%', className}) => {
  const [errorRef, {height: errorHeight}] = useMeasure<HTMLDivElement>();

  return (
    <S.Error className={className} height={`${errorHeight}px`} width={width}>
      <S.ErrorContent ref={errorRef}>{children}</S.ErrorContent>
    </S.Error>
  );
};

const formField = <P extends TFieldHandlers = TFieldHandlers, V = unknown>(
  Component: React.ComponentType<P>,
  payloadSettings?: Partial<TSettings>,
) =>
  React.memo<P & TProps<P, V>>(
    // eslint-disable-next-line complexity
    ({
      id: propId,
      className,
      label,
      actions,
      footer = null,
      noMargin,
      showHeader,
      showError,
      input,
      meta,
      isGroupViewType = false,
      disabled,
      readOnly,
      width,
      ...other
    }) => {
      const settings: TSettings = merge({}, defaultSettings, payloadSettings, {
        noMargin,
        showHeader,
        showError,
        width,
      });

      const isRenderHeader = settings.showHeader && (label || actions);

      const id = propId || input.name;
      const isInvalid = !readOnly && ((meta.error && meta.touched) || other.isInvalid);
      const componentProps: P = {
        ...(other as P),
        id,
        disabled,
        readOnly,
        type: input.type,
        name: input.name,
        value: input.value,
        checked: input.checked,
        multiple: input.multiple,
        label: settings.showHeader ? undefined : label,
        isInvalid,
        onChange: (...args) => {
          if (isFunction(other.onChange)) {
            other.onChange(...args);
          }
          input.onChange(...args);
        },
        onFocus: (...args) => {
          if (isFunction(other.onFocus)) {
            other.onFocus(...args);
          }
          input.onFocus(...args);
        },
        onBlur: (...args) => {
          if (isFunction(other.onBlur)) {
            other.onBlur(...args);
          }
          input.onBlur(...args);
        },
      };

      return (
        <FormField
          width={
            typeof settings.width === 'function' ? settings.width(componentProps) : settings.width
          }
          type={input.type}
          name={input.name}
          isGroupViewType={isGroupViewType}
          className={className}
          noMargin={settings.noMargin}
        >
          {isRenderHeader && (
            <Header>
              {label && (
                <Label htmlFor={id} disabled={disabled}>
                  {label}
                </Label>
              )}
              {actions && <div>{actions}</div>}
            </Header>
          )}
          <Component {...componentProps} />
          {footer}
          {settings.showError && <FieldError>{isInvalid && meta.error}</FieldError>}
        </FormField>
      );
    },
  );

export const newFormField = <P extends TComponentProps = TComponentProps>(
  Component: React.ComponentType<P>,
  payloadSettings?: Partial<TSettings>,
) =>
  React.memo<P & TNewProps>(
    // eslint-disable-next-line complexity
    ({
      id: propId,
      className,
      label,
      actions,
      disabled,
      readOnly,
      name,
      noMargin,
      showHeader,
      showError,
      footer = null,
      isGroupViewType = false,
      onChange = noop,
      onBlur = noop,
      onFocus = noop,
      mapValue,
      width,
      ...other
    }) => {
      const {t} = useTranslation();
      const {
        field,
        fieldState: {error},
      } = useController({name});
      const settings: TSettings = merge({}, defaultSettings, payloadSettings, {
        noMargin,
        showHeader,
        showError,
        width,
      });

      const id = propId || name;
      const isInvalid = !readOnly && (error || other.isInvalid);
      const isRenderHeader = settings.showHeader && (label || actions);

      const handleChange: P['onChange'] = React.useCallback(
        (...args) => {
          if (!mapValue) {
            onChange(...args);
            field.onChange(...args);

            return;
          }

          if (args[0]?.target?.value !== undefined) {
            const mappedEvent = {
              ...args[0],
              target: {
                ...args[0].target,
                value: mapValue(args[0].target.value),
              },
            };

            onChange(mappedEvent);
            field.onChange(mappedEvent);

            return;
          }

          const mappedValue = mapValue(args[0]);

          onChange(mappedValue);
          field.onChange(mappedValue);
        },
        [field, onChange, mapValue],
      );

      const handleBlur: P['onBlur'] = React.useCallback(
        (...args) => {
          onBlur(...args);
          field.onBlur();
        },
        [field, onBlur],
      );

      const handleFocus: P['onFocus'] = React.useCallback(
        (...args) => {
          onFocus(...args);
        },
        [onFocus],
      );
      const componentProps: P = {
        ...(other as P),
        innerRef: other.innerRef ? mergeRefs(field.ref, other.innerRef) : field.ref,
        id,
        disabled,
        readOnly,
        name,
        isInvalid,
        value: field.value,
        inputValue: other.value,
        label: settings.showHeader ? undefined : label,
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: handleFocus,
        isLabelledBy: settings.isLabelledBy,
      };

      return (
        <FormField
          width={
            typeof settings.width === 'function' ? settings.width(componentProps) : settings.width
          }
          name={name}
          isGroupViewType={isGroupViewType}
          className={className}
          noMargin={settings.noMargin}
        >
          {isRenderHeader && (
            <Header>
              {label && (
                <Label
                  {...(!settings.isLabelledBy ? {htmlFor: id} : {})}
                  {...(settings.isLabelledBy ? {id} : {})}
                  disabled={disabled}
                >
                  {label}
                </Label>
              )}
              {actions && <div>{actions}</div>}
            </Header>
          )}
          <Component {...componentProps} />
          {footer}
          {settings.showError && (
            <FieldError>{isInvalid && t(error?.message ?? '', error?.ref?.value)}</FieldError>
          )}
        </FormField>
      );
    },
  );

export default formField;

export {FieldError};
/* eslint-enable react/jsx-props-no-spreading */
